import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

const CHEIE_RELOAD = 'rentcore_chunk_reload_ts'
// O nouă reîncărcare automată e permisă abia după acest interval — dacă și după reload
// chunk-ul lipsește (deploy stricat, rețea căzută), arătăm eroarea în loc să intrăm în buclă.
const FEREASTRA_ANTI_BUCLA_MS = 30_000

/** Eroare de încărcare a unui chunk dinamic (tipic: deploy nou, fișierele vechi nu mai există). */
export function esteEroareChunk(err: unknown): boolean {
  const e = err as { name?: unknown; message?: unknown } | null
  const msg = String(e?.message ?? err ?? '')
  return e?.name === 'ChunkLoadError'
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
    || /Unable to preload CSS/i.test(msg)
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg)
}

/**
 * Reîncarcă pagina O SINGURĂ dată pe fereastră de timp (flag în sessionStorage).
 * Întoarce `false` dacă reîncărcarea a fost deja încercată recent — apelantul arată eroarea.
 */
export function reincarcaOData(): boolean {
  try {
    const ultima = Number(sessionStorage.getItem(CHEIE_RELOAD) ?? 0)
    if (ultima && Date.now() - ultima < FEREASTRA_ANTI_BUCLA_MS) return false
    sessionStorage.setItem(CHEIE_RELOAD, String(Date.now()))
  } catch {
    // sessionStorage indisponibil → nu putem garanta lipsa buclei; nu reîncărcăm automat.
    return false
  }
  window.location.reload()
  return true
}

interface Props {
  children: React.ReactNode
  /** Schimbarea cheii (ex. pagina curentă) resetează boundary-ul. */
  resetKey?: string
}

interface State {
  eroare: Error | null
}

/**
 * Fără un error boundary, orice excepție de randare dintr-o pagină demonta TOT arborele React
 * → ecran alb, fără niciun mesaj, inclusiv bara laterală. Boundary-ul izolează pagina: restul
 * aplicației rămâne funcțional, iar utilizatorul primește un buton „Reîncarcă".
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { eroare: null }

  static getDerivedStateFromError(eroare: Error): State {
    return { eroare }
  }

  componentDidCatch(eroare: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', eroare, info.componentStack)
    // După un deploy, chunk-urile paginilor vechi nu mai există pe server: importul dinamic
    // eșuează. O reîncărcare aduce index.html nou cu referințele corecte.
    if (esteEroareChunk(eroare)) reincarcaOData()
  }

  componentDidUpdate(prev: Props) {
    if (this.state.eroare && prev.resetKey !== this.props.resetKey) this.setState({ eroare: null })
  }

  render() {
    const { eroare } = this.state
    if (!eroare) return this.props.children
    const chunk = esteEroareChunk(eroare)
    return (
      <div role="alert" className="flex flex-col items-center justify-center text-center gap-3 px-6 py-24">
        <div className="w-12 h-12 rounded-xl bg-red-900/30 border border-red-800/50 flex items-center justify-center">
          <AlertTriangle size={22} className="text-red-400" />
        </div>
        <p className="text-base font-semibold text-white">
          {chunk ? 'A apărut o versiune nouă a aplicației' : 'Pagina a întâmpinat o eroare'}
        </p>
        <p className="text-sm text-slate-400 max-w-md">
          {chunk
            ? 'Fișierele acestei pagini nu au putut fi încărcate. Reîncarcă aplicația pentru a primi versiunea curentă.'
            : 'Datele tale salvate nu sunt afectate. Reîncarcă pagina; dacă problema persistă, anunță administratorul.'}
        </p>
        <div className="flex flex-wrap justify-center gap-2 mt-1">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors"
          >
            <RefreshCw size={14} /> Reîncarcă
          </button>
          {!chunk && (
            <button
              onClick={() => this.setState({ eroare: null })}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm transition-colors"
            >
              Încearcă din nou
            </button>
          )}
        </div>
      </div>
    )
  }
}
